import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  CreateFolderUseCaseInput,
  CreateFormUseCaseInput,
  FolderSummary,
  FolderSummaryProps,
  FormSummary,
  SpaceObjects,
  UpdateFolderUseCaseInput,
  UpdateFormUseCaseInput,
  ParentFolder,
  FormSummaryProps,
  FormStatus,
  GlobalForm,
  Tag,
  FormCategory,
  CLONE_FORM_USE_CASE,
  FIND_FORM_BY_ID_USE_CASE,
  Form,
} from '@module/form';
import { FindFormByIdUseCaseState, SpacePageState } from './space.reducer';
import { REQUEST_STATUS } from '@utils';
import { CloneFormUseCaseState } from 'redux/explore';

const mapStateToSpaceObjects = (
  forms: FormSummary[],
  folders: FolderSummary[],
  parentFolders: ParentFolder[],
): SpaceObjects => {
  return SpaceObjects.create({
    forms,
    folders,
    parentFolders,
  }).getValue();
};

const addNewFolderToSpaceObjects = (
  spaceObjects: SpaceObjects,
  newFolderSummary: FolderSummary,
): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;
  const changedFolderSummaries: FolderSummary[] = [...folders, newFolderSummary];
  return mapStateToSpaceObjects(forms, changedFolderSummaries, parentFolders);
};

const updateFolderFromSpacePage = (
  spaceObjects: SpaceObjects,
  updatedFolderSummary: UpdateFolderUseCaseInput,
): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;
  const newFoldersSummaries: FolderSummary[] = folders.map((folder: FolderSummary) => {
    if (folder.id.toString() === updatedFolderSummary.id.toString()) {
      const { input } = updatedFolderSummary;
      if (input.title) {
        folder.updateTitle(input.title);
        folder.updatedAt = Date.now().toString();
      }
    }
    return folder;
  });
  return mapStateToSpaceObjects(forms, newFoldersSummaries, parentFolders);
};

const deleteFolderFromSpaceObjects = (spaceObjects: SpaceObjects, folderId: string) => {
  const { forms, folders, parentFolders } = spaceObjects;
  const changedFolderSummaries: FolderSummary[] = folders.filter((folder: FolderSummary) => {
    const isNotDeletedFolder = folder.id.toString() !== folderId;
    const isNotSubFolderOfDeletedFolder = folder.parentId !== folderId;

    return isNotDeletedFolder && isNotSubFolderOfDeletedFolder;
  });

  const changedFormSummaries: FormSummary[] = forms.filter((form: FormSummary) => {
    const isNotSubFormOfDeletedFolder = form.folderId !== folderId;

    return isNotSubFormOfDeletedFolder;
  });

  return mapStateToSpaceObjects(changedFormSummaries, changedFolderSummaries, parentFolders);
};

const addNewFormToSpaceObjects = (
  spaceObjects: SpaceObjects,
  newFormSummary: FormSummary,
): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;
  const changedFormSummaries: FormSummary[] = [newFormSummary, ...forms];
  return mapStateToSpaceObjects(changedFormSummaries, folders, parentFolders);
};

const updateFormFromSpaceObjects = (
  spaceObjects: SpaceObjects,
  useCaseInput: UpdateFormUseCaseInput,
): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;
  const { input } = useCaseInput;

  // Form left this folder — remove it from the current list
  if (input.folderId !== undefined) {
    return deleteFormFromSpaceObjects(spaceObjects, useCaseInput.id);
  }

  const newFormSummaries: FormSummary[] = forms.map((form: FormSummary) => {
    if (form.id.toString() === useCaseInput.id) {
      const { title, status } = input;
      if (title) {
        form.updateTitle(input.title);
        form.updatedAt = Date.now().toString();
      }
      if (status) {
        form.updateStatus(status);
      }
    }

    return form;
  });

  return mapStateToSpaceObjects(newFormSummaries, folders, parentFolders);
};

const updateFormsFromSpaceObjects = (
  spaceObjects: SpaceObjects,
  useCaseInputs: UpdateFormUseCaseInput[],
): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;

  const newFormSummaries: FormSummary[] = forms.map((form: FormSummary) => {
    const foundForm = useCaseInputs.find((input) => input.id === form.id.toString());
    if (foundForm) {
      const { input } = foundForm;
      const { title, status } = input;
      if (title) {
        form.updateTitle(input.title);
        form.updatedAt = Date.now().toString();
      }
      if (status) {
        form.updateStatus(status);
      }
    }

    return form;
  });

  return mapStateToSpaceObjects(newFormSummaries, folders, parentFolders);
};

const deleteFormFromSpaceObjects = (spaceObjects: SpaceObjects, formId: string): SpaceObjects => {
  const { forms, folders, parentFolders } = spaceObjects;
  const changedFormSummaries: FormSummary[] = forms.filter(
    (form: FormSummary) => form.id.toString() !== formId,
  );
  return mapStateToSpaceObjects(changedFormSummaries, folders, parentFolders);
};

export const handleSearchSpaceObjectsUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      searchFormsAndFoldersStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;

    return {
      ...state,
      formsAndFoldersSearchOptions: payload as SpaceObjects,
      searchFormsAndFoldersStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      searchFormsAndFoldersStatus: RequestStatus.ERROR,
      error: error,
    };
  },
  reset: (state: SpacePageState) => {
    return { ...state };
  },
};

export const handleMoveFolderPickerUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      moveFolderPickerStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    return {
      ...state,
      moveFolderPickerSpaceObjects: action.payload as SpaceObjects,
      moveFolderPickerStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    return {
      ...state,
      moveFolderPickerStatus: RequestStatus.ERROR,
      error: action.error,
    };
  },
  reset: (state: SpacePageState) => {
    return {
      ...state,
      moveFolderPickerStatus: RequestStatus.RESET,
      moveFolderPickerSpaceObjects: undefined,
    };
  },
};

export const handleGetFolderByIdUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      getFolderByIdStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const spaceObjects = action.payload as SpaceObjects;
    const { forms } = spaceObjects;
    const importingFormIds = forms
      .filter((form) => form.status === FormStatus.Processing)
      .map((form) => form.id.toString());

    return {
      ...state,
      spaceObjects,
      getFolderByIdStatus: RequestStatus.SUCCESS,
      importingFormIds,
      folderNextToken: spaceObjects.folderNextToken,
      formNextToken: spaceObjects.formNextToken,
      loadingMoreFoldersStatus: RequestStatus.SUCCESS,
      loadingMoreFormsStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      getFolderByIdStatus: RequestStatus.ERROR,
      error: error,
    };
  },
  reset: (state: SpacePageState) => {
    return { ...state };
  },
};

export const handleGetTagUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      getTagsStatus: RequestStatus.EXECUTE,
      isCreateFormButtonLoading: true,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;

    return {
      ...state,
      tags: payload as Tag[],
      getTagsStatus: RequestStatus.SUCCESS,
      isCreateFormButtonLoading: false,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      getTagsStatus: RequestStatus.ERROR,
      isCreateFormButtonLoading: false,
    };
  },
};

export const handleGetFormCategoriesUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      getFormCategoriesStatus: REQUEST_STATUS.EXECUTE,
      isCreateFormButtonLoading: true,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;

    return {
      ...state,
      formCategories: payload as FormCategory[],
      getFormCategoriesStatus: REQUEST_STATUS.SUCCESS,
      isCreateFormButtonLoading: false,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      getFormCategoriesStatus: REQUEST_STATUS.ERROR,
      isCreateFormButtonLoading: false,
    };
  },
};

export const handleCreateFolderUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const input = action.payload as CreateFolderUseCaseInput;

    const createdFolderSummaryProps: FolderSummaryProps = {
      ...input,
    };
    const createdFolderSummary = FolderSummary.create(createdFolderSummaryProps).getValue();

    return {
      ...state,
      createdFolderSummary: createdFolderSummary,
      createFolderStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    const spaceObjects = addNewFolderToSpaceObjects(state.spaceObjects, state.createdFolderSummary);
    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects, // Sync search options
      createFolderStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;

    return {
      ...state,
      error: error,
      createFolderStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      createFolderStatus: RequestStatus.RESET,
    };
  },
};

export const handleUpdateFolderUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;
    return {
      ...state,
      updatedFolderSummary: payload as UpdateFolderUseCaseInput,
      updateFolderSummaryStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    const spaceObjects: SpaceObjects = updateFolderFromSpacePage(
      state.spaceObjects,
      state.updatedFolderSummary,
    );

    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects,
      updateFolderSummaryStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      updateFolderSummaryStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      updateFolderSummaryStatus: RequestStatus.RESET,
    };
  },
};

export const handleDeleteFolderUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;

    return {
      ...state,
      deletedFolderId: payload as string,
      deleteFolderStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    const spaceObjects = deleteFolderFromSpaceObjects(
      state.spaceObjects,
      state.deletedFolderId as string,
    );
    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects,
      deleteFolderStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      deleteFolderStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: any) => {
    return {
      ...state,
      deleteFolderStatus: RequestStatus.RESET,
    };
  },
};

export const handleCreateFormUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const input = action.payload as CreateFormUseCaseInput;

    const formSummaryProps: FormSummaryProps = {
      id: input.id,
      folderId: input.folderId,
      title: input.title,
      updatedAt: Date.now().toString(),
      status: FormStatus.Active,
      tags:
        input.tags?.map((tagId: string) =>
          Tag.create({
            id: tagId,
            key: state.tags?.find((tag: Tag) => tag.id.toString() === tagId)?.key || '',
          }).getValue(),
        ) || [],
      category: input.categoryInstance || null,
    };
    const createdFormSummary = FormSummary.create(formSummaryProps).getValue();
    const spaceObjects = state.spaceObjects;
    if (spaceObjects) {
      spaceObjects.forms.push(createdFormSummary);
    }

    return {
      ...state,
      newFormId: input.id,
      createdFormSummary,
      spaceObjects,
      createFormStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      formsAndFoldersSearchOptions: state.spaceObjects,
      createFormStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    const spaceObjects = state.spaceObjects;
    if (spaceObjects) {
      spaceObjects.removeForm(state.newFormId);
    }

    return {
      ...state,
      error: error,
      spaceObjects,
      createFormStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      createFormStatus: RequestStatus.RESET,
    };
  },
};

export const handleUpdateFormTitleUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    return {
      ...state,
      updatedFormSummary: action.payload as UpdateFormUseCaseInput,
      updateFormSummaryStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    const spaceObjects = updateFormFromSpaceObjects(state.spaceObjects, state.updatedFormSummary);
    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects,
      updateFormSummaryStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      updateFormSummaryStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      updateFormSummaryStatus: RequestStatus.RESET,
    };
  },
};

export const handleDeleteFormUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;

    return {
      ...state,
      deletedFormId: payload as string,
      deleteFormStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState): SpacePageState => {
    const spaceObjects = deleteFormFromSpaceObjects(
      state.spaceObjects,
      state.deletedFormId as string,
    );

    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects,
      deleteFormStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      deleteFormStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: any) => {
    return {
      ...state,
      deleteFormStatus: RequestStatus.RESET,
    };
  },
};

export const handleGetGlobalFormsUseCase = {
  executing: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      getGlobalFormsStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;
    return {
      ...state,
      globalForms: payload as GlobalForm[],
      getGlobalFormsStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      getGlobalFormsStatus: RequestStatus.ERROR,
      error: error,
    };
  },
  reset: (state: SpacePageState) => {
    return { ...state };
  },
};

export const handleCloneFormUseCase: UseCaseStateGenerator<CloneFormUseCaseState> = {
  name: CLONE_FORM_USE_CASE,
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    return {
      ...state,
      clonedFormStatus: RequestStatus.EXECUTE,
      updatedFormSummary: action.payload as UpdateFormUseCaseInput,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;
    const clonedForm = payload as FormSummary;
    const spaceObjects = state.spaceObjects?.addNewForm(clonedForm);
    return {
      ...state,
      spaceObjects,
      formsAndFoldersSearchOptions: spaceObjects,
      clonedFormStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return { ...state, error: error, cloneFormStatus: RequestStatus.ERROR };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return {
      ...state,
      clonedFormStatus: RequestStatus.RESET,
    };
  },
};

export const handleFindFormByIdUseCase: UseCaseStateGenerator<FindFormByIdUseCaseState> = {
  name: FIND_FORM_BY_ID_USE_CASE,
  executing: (state: SpacePageState): SpacePageState => {
    return { ...state, findFormByIdStatus: RequestStatus.EXECUTE };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const { payload } = action;
    const form = payload as Form;

    return {
      ...state,
      formSelected: form,
      hasEssayOrFillInBlankQuestion: form.hasEssayOrFillInBlankQuestion,
      findFormByIdStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error,
      findFormByIdStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: SpacePageState): SpacePageState => {
    return { ...state, findFormByIdStatus: RequestStatus.RESET };
  },
};

export const handleLoadMoreSpaceObjectsUseCase = {
  executing: (state: SpacePageState, action: Action): SpacePageState => {
    const { type } = action.payload as { type: 'folder' | 'form' };
    return {
      ...state,
      loadingMoreFoldersStatus: type === 'folder' ? RequestStatus.EXECUTE : state.loadingMoreFoldersStatus,
      loadingMoreFormsStatus: type === 'form' ? RequestStatus.EXECUTE : state.loadingMoreFormsStatus,
    };
  },
  success: (state: SpacePageState, action: Action): SpacePageState => {
    const newSpaceObjects = action.payload as SpaceObjects;
    const existingFolders = state.spaceObjects?.folders ?? [];
    const existingForms = state.spaceObjects?.forms ?? [];
    const existingParentFolders = state.spaceObjects?.parentFolders ?? [];

    const mergedFolders = [...existingFolders, ...newSpaceObjects.folders];
    const mergedForms = [...existingForms, ...newSpaceObjects.forms];

    const mergedSpaceObjects = SpaceObjects.create({
      parentFolders: existingParentFolders,
      folders: mergedFolders,
      forms: mergedForms,
    }).getValue();

    return {
      ...state,
      spaceObjects: mergedSpaceObjects,
      folderNextToken: newSpaceObjects.folderNextToken,
      formNextToken: newSpaceObjects.formNextToken,
      loadingMoreFoldersStatus: RequestStatus.SUCCESS,
      loadingMoreFormsStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: SpacePageState, action: Action): SpacePageState => {
    const { error } = action;
    return {
      ...state,
      error,
      loadingMoreFoldersStatus: RequestStatus.ERROR,
      loadingMoreFormsStatus: RequestStatus.ERROR,
    };
  },
};
